import { ReactElement, Suspense, useEffect, useMemo, useState } from 'react';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import { FullLoader, IconButton, NumberInput, Switch } from '@sc/shared-ui';
import { useMutation, useSuspenseQuery } from '@apollo/client/react';
import {
  InformationCircleIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { Tooltip } from 'react-tooltip';
import { NotificationPreferenceType } from '@sc/graphql';
import { useUser } from '../providers/users';
import { useAlerts } from '../providers/alerts';
import {
  GQL_MUTATION_CREATE_NOTIFICATION_PREFERENCE,
  GQL_MUTATION_DELETE_NOTIFICATION_PREFERENCE,
  GQL_MUTATION_UPDATE_NOTIFICATION_PREFERENCE,
  GQL_QUERY_NOTIFICATION_PREFERENCES,
} from '../utility/queries';
import { useLocation, useNavigate } from '@tanstack/react-router';
import classNames from 'classnames';
import { track } from '../utility/analytics';

type ChannelType = 'email' | 'web';

type BillingRow = {
  uuid: string;
  value: string;
  isWeb: boolean;
  isEmail: boolean;
};

const ChannelHeader = ({
  heading,
  withDelete,
}: {
  heading?: string;
  withDelete?: boolean;
}): ReactElement => (
  <div
    className={classNames('grid  gap-4 px-3 py-2 border-b border-grey-t-700', {
      'grid-cols-[1fr_80px_80px]': !withDelete,
      'grid-cols-[1fr_80px_80px_40px]': withDelete,
    })}
  >
    {heading ? (
      <span className="text-text-low-priority text-sm font-semibold">
        {heading}
      </span>
    ) : (
      <span />
    )}
    <span className="text-text-low-priority text-sm font-semibold text-center">
      Email
    </span>
    <span className="text-text-low-priority text-sm font-semibold text-center inline-flex items-center justify-center gap-1">
      Web
      <InformationCircleIcon
        className="size-4"
        data-tooltip-id="settings-app-info"
        data-tooltip-content="App notifications appear in the bell menu in the top bar."
      />
      <Tooltip id="settings-app-info" className="z-20" />
    </span>
  </div>
);

const ChannelToggleRow = ({
  label,
  description,
  emailChecked,
  appChecked,
  disabled,
  onChange,
  withDelete,
}: {
  label: string;
  description: string;
  emailChecked: boolean;
  appChecked: boolean;
  disabled?: boolean;
  onChange: (channel: ChannelType, enabled: boolean) => void;
  withDelete?: boolean;
}): ReactElement => (
  <div
    className={classNames(
      'grid grid-cols-[1fr_80px_80px] gap-4 items-center px-3 py-3 border-b border-grey-t-800',
      {
        'grid-cols-[1fr_80px_80px]': !withDelete,
        'grid-cols-[1fr_80px_80px_40px]': withDelete,
      }
    )}
  >
    <div className="space-y-1">
      <span className="font-semibold block">{label}</span>
      <p className="text-text-low-priority text-sm">{description}</p>
    </div>
    {(['email', 'web'] as const).map((channel) => (
      <div key={channel} className="flex justify-center">
        <Switch
          size="sm"
          enabled={channel === 'email' ? emailChecked : appChecked}
          disabled={!!disabled}
          setEnabled={(enabled) => onChange(channel, enabled)}
        />
      </div>
    ))}
  </div>
);

const BillingValueRow = ({
  initialValue,
  emailChecked,
  appChecked,
  thresholdSuffix,
  thresholdIcon,
  thresholdMin,
  thresholdMax,
  thresholdPlaceholder,
  disabled,
  thresholdDisabled,
  onCommitValue,
  onToggleChannel,
  onDelete,
}: {
  initialValue: string;
  emailChecked: boolean;
  appChecked: boolean;
  thresholdSuffix?: string | undefined;
  thresholdIcon?: HeroIconType | undefined;
  thresholdMin?: number | undefined;
  thresholdMax?: number | undefined;
  thresholdPlaceholder?: string | undefined;
  disabled: boolean;
  thresholdDisabled: boolean;
  onCommitValue: (next: string) => void;
  onToggleChannel: (channel: ChannelType, next: boolean) => void;
  onDelete: () => void;
}): ReactElement => {
  const [draft, setDraft] = useState(initialValue);

  useEffect(() => {
    setDraft(initialValue);
  }, [initialValue]);

  return (
    <div className="grid grid-cols-[1fr_80px_80px_40px] gap-4 items-center px-3 py-3 border-b border-grey-t-800">
      <div className="max-w-45">
        <NumberInput
          value={draft}
          placeholder={thresholdPlaceholder ?? ''}
          disabled={thresholdDisabled}
          min={thresholdMin}
          max={thresholdMax}
          Icon={thresholdIcon}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            if (draft.trim() !== initialValue) onCommitValue(draft.trim());
          }}
        />
      </div>
      <div className="flex justify-center">
        <Switch
          size="sm"
          enabled={emailChecked}
          disabled={disabled}
          setEnabled={(next) => onToggleChannel('email', next)}
        />
      </div>
      <div className="flex justify-center">
        <Switch
          size="sm"
          enabled={appChecked}
          disabled={disabled}
          setEnabled={(next) => onToggleChannel('web', next)}
        />
      </div>
      <div className="flex justify-center">
        <IconButton
          Icon={TrashIcon}
          type="delete"
          size="small"
          onClick={onDelete}
          disabled={disabled}
        />
      </div>
    </div>
  );
};

const BillingSection = ({
  label,
  description,
  notificationType,
  thresholdSuffix,
  thresholdIcon,
  thresholdMin,
  thresholdMax,
  thresholdPlaceholder,
  disabled,
  rows,
  onCreate,
  onUpdate,
  onDelete,
}: {
  label: string;
  description: string;
  notificationType: NotificationPreferenceType;
  thresholdSuffix?: string;
  thresholdIcon?: HeroIconType;
  thresholdMin?: number;
  thresholdMax?: number;
  thresholdPlaceholder?: string;
  disabled: boolean;
  rows: BillingRow[];
  onCreate: (args: {
    notificationType: NotificationPreferenceType;
    notificationValue: string;
    isWeb: boolean;
    isEmail: boolean;
  }) => void;
  onUpdate: (args: {
    row: BillingRow;
    isWeb: boolean;
    isEmail: boolean;
  }) => void;
  onDelete: (uuid: string) => void;
}): ReactElement => {
  const handleAdd = (): void => {
    // first find the highest notification value to add 100, then make sure it's below 1000
    const candidate =
      Math.max(
        ...rows.map((v) => {
          const number = parseInt(v.value);
          if (isNaN(number)) return 0;
          return number;
        }),
        0
      ) + 100;

    const notificationValue = String(thresholdMax
      ? Math.min(candidate, thresholdMax)
      : candidate);

    onCreate({
      notificationType,
      notificationValue,
      isWeb: false,
      isEmail: false,
    });
  };

  const renameRow = (row: BillingRow, nextRaw: string): void => {
    const next = nextRaw.trim();

    if (next === '' || next === row.value) return;

    onDelete(row.uuid);

    onCreate({
      notificationType,
      notificationValue: String(next),
      isWeb: row.isWeb,
      isEmail: row.isEmail,
    });
  };

  const toggleChannel = (
    row: BillingRow,
    channel: ChannelType,
    nextOn: boolean
  ): void => {
    const isNextEmail = channel === 'email' ? nextOn : row.isEmail;
    const isNextWeb = channel === 'web' ? nextOn : row.isWeb;

    onUpdate({ row, isWeb: isNextWeb, isEmail: isNextEmail });
  };

  return (
    <div className="border-b border-grey-t-800">
      <div className="flex items-center justify-between px-3 pt-4 pb-2">
        <div>
          <span className="font-semibold block">{label}</span>
          <p className="text-text-low-priority text-sm">{description}</p>
        </div>
        <IconButton
          Icon={PlusIcon}
          onClick={handleAdd}
          disabled={disabled || rows.length >= 3}
          size="small"
        />
      </div>
      {rows.length > 0 && <ChannelHeader withDelete heading="Threshold" />}
      {rows.map((row) => (
        <BillingValueRow
          key={row.uuid}
          initialValue={row.value}
          emailChecked={row.isEmail}
          appChecked={row.isWeb}
          thresholdSuffix={thresholdSuffix}
          thresholdIcon={thresholdIcon}
          thresholdMin={thresholdMin}
          thresholdMax={thresholdMax}
          thresholdPlaceholder={thresholdPlaceholder}
          disabled={disabled}
          thresholdDisabled={disabled}
          onCommitValue={(next) => renameRow(row, next)}
          onToggleChannel={(channel, on) => toggleChannel(row, channel, on)}
          onDelete={() => onDelete(row.uuid)}
        />
      ))}
    </div>
  );
};

const SettingsNotificationsTabContent = (): ReactElement => {
  const { isAdmin } = useUser();
  const { useApolloError } = useAlerts();
  const onError = useApolloError();

  const { data } = useSuspenseQuery(GQL_QUERY_NOTIFICATION_PREFERENCES);
  const [createPreference] = useMutation(
    GQL_MUTATION_CREATE_NOTIFICATION_PREFERENCE,
    {
      update: (cache, { data: result }) => {
        if (!result) return;

        cache.updateQuery(
          { query: GQL_QUERY_NOTIFICATION_PREFERENCES },
          (existing) =>
            existing
              ? {
                  notificationPreferences: [
                    ...existing.notificationPreferences.filter(
                      (p) => p.uuid !== result.createNotificationPreference.uuid
                    ),
                    result.createNotificationPreference,
                  ],
                }
              : existing
        );
      },
    }
  );

  const [updatePreference] = useMutation(
    GQL_MUTATION_UPDATE_NOTIFICATION_PREFERENCE
  );

  const [deletePreference] = useMutation(
    GQL_MUTATION_DELETE_NOTIFICATION_PREFERENCE,
    {
      update: (cache, _, { variables }) => {
        const uuid = variables?.input.uuid;

        if (!uuid) return;

        cache.updateQuery(
          { query: GQL_QUERY_NOTIFICATION_PREFERENCES },
          (existing) =>
            existing
              ? {
                  notificationPreferences:
                    existing.notificationPreferences.filter(
                      (p) => p.uuid !== uuid
                    ),
                }
              : existing
        );
      },
    }
  );

  const preferences = data.notificationPreferences;

  const projectReadyPref = preferences.find(
    (p) => p.notificationType === NotificationPreferenceType.projectReady
  );

  const createPref = ({
    notificationType,
    notificationValue,
    isWeb,
    isEmail,
  }: {
    notificationType: NotificationPreferenceType;
    notificationValue: string | null;
    isWeb: boolean;
    isEmail: boolean;
  }): void => {
    track('Notification Preference Changed', {
      action: 'created',
      notificationType,
    });

    void createPreference({
      variables: {
        input: { notificationType, notificationValue, isWeb, isEmail },
      },
      optimisticResponse: {
        createNotificationPreference: {
          __typename: 'NotificationPreference',
          uuid: `optimistic-${notificationType}-${
            notificationValue ?? 'null'
          }-${Date.now()}`,
          notificationType,
          notificationValue,
          isWeb,
          isEmail,
        },
      },
      onError,
    });
  };

  const updatePref = ({
    row,
    isWeb,
    isEmail,
  }: {
    row: {
      uuid: string;
      notificationType: NotificationPreferenceType;
      notificationValue: string | null;
    };
    isWeb: boolean;
    isEmail: boolean;
  }): void => {
    track('Notification Preference Changed', {
      action: 'updated',
      notificationType: row.notificationType,
    });

    void updatePreference({
      variables: { input: { uuid: row.uuid, isWeb, isEmail } },
      optimisticResponse: {
        updateNotificationPreference: {
          __typename: 'NotificationPreference',
          uuid: row.uuid,
          notificationType: row.notificationType,
          notificationValue: row.notificationValue,
          isWeb,
          isEmail,
        },
      },
      onError,
    });
  };

  const deletePref = (uuid: string): void => {
    const pref = preferences.find((p) => p.uuid === uuid);

    if (pref)
      track('Notification Preference Changed', {
        action: 'deleted',
        notificationType: pref.notificationType,
      });

    void deletePreference({
      variables: { input: { uuid } },
      optimisticResponse: { deleteNotificationPreference: true },
      onError,
    });
  };

  const billingRows = (
    notificationType: NotificationPreferenceType
  ): BillingRow[] =>
    preferences
      .filter((p) => p.notificationType === notificationType)
      .map((p) => ({
        uuid: p.uuid,
        value: p.notificationValue ?? '',
        isWeb: p.isWeb,
        isEmail: p.isEmail,
      }))
      .sort((a, b) => Number(a.value) - Number(b.value));

  const { hash } = useLocation();

  const [selectedParentIdx, selectedChildIdx] = useMemo(() => {
    const [parent, child] = hash.split('|');
    if (!parent) return [0, 0]; // technicall this will never happen but occasionaly this gets called on navigate
    return [parseInt(parent), child ? parseInt(child) : 0];
  }, [hash]);

  const navigate = useNavigate();

  return (
    <TabGroup
      selectedIndex={selectedChildIdx}
      onChange={(idx) =>
        void navigate({
          to: '/settings',
          hash: `${selectedParentIdx}|${idx}`,
        })
      }
      vertical
      className="flex h-full"
    >
      <TabList className="flex flex-col w-48 shrink-0 space-y-2 border-r border-grey-t-800 p-2 h-full">
        <Tab className="text-left px-3 py-2 rounded-xs font-semibold data-selected:bg-primary-t-600/10 data-selected:text-primary-300 hover:bg-grey-t-900 outline-none">
          Knowledge base
        </Tab>
        <Tab className="text-left px-3 py-2 rounded-xs font-semibold data-selected:bg-primary-t-600/10 data-selected:text-primary-300 hover:bg-grey-t-900 outline-none">
          Usage
        </Tab>
      </TabList>
      <TabPanels className="grow">
        <TabPanel>
          <ChannelHeader />
          <ChannelToggleRow
            label="Ready to query"
            description="Get notified when a knowledge base finishes processing and is ready to query."
            emailChecked={projectReadyPref?.isEmail ?? false}
            appChecked={projectReadyPref?.isWeb ?? false}
            onChange={(channel, enabled) =>
              projectReadyPref
                ? updatePref({
                    row: {
                      uuid: projectReadyPref.uuid,
                      notificationType: projectReadyPref.notificationType,
                      notificationValue: projectReadyPref.notificationValue,
                    },
                    isWeb: channel === 'web' ? enabled : projectReadyPref.isWeb,
                    isEmail:
                      channel === 'email' ? enabled : projectReadyPref.isEmail,
                  })
                : createPref({
                    notificationType: NotificationPreferenceType.projectReady,
                    notificationValue: null,
                    isWeb: channel === 'web',
                    isEmail: channel === 'email',
                  })
            }
          />
        </TabPanel>
        <TabPanel>
          {!isAdmin && (
            <p className="text-text-low-priority text-sm px-5 py-3">
              Usage alerts can only be configured by an admin or the account
              owner.
            </p>
          )}
          <BillingSection
            label="Queries remaining"
            description="Alert when you reach the number of queries remaining for any subscription period, between 10 - 1000."
            notificationType={NotificationPreferenceType.creditConsumption}
            thresholdMin={10}
            thresholdMax={1000}
            thresholdPlaceholder="80"
            disabled={!isAdmin}
            rows={billingRows(NotificationPreferenceType.creditConsumption)}
            onCreate={createPref}
            onUpdate={({ row, isWeb, isEmail }) =>
              updatePref({
                row: {
                  uuid: row.uuid,
                  notificationType:
                    NotificationPreferenceType.creditConsumption,
                  notificationValue: row.value === '' ? null : row.value,
                },
                isWeb,
                isEmail,
              })
            }
            onDelete={deletePref}
          />
        </TabPanel>
      </TabPanels>
    </TabGroup>
  );
};

export const SettingsNotificationsTab = (): ReactElement => (
  <Suspense fallback={<FullLoader className="size-6" />}>
    <div className="grow flex flex-col">
      <SettingsNotificationsTabContent />
    </div>
  </Suspense>
);
